import {
  DEFAULT_MIN_ZOOM_TO_VIEW_LABELS,
  LABEL_TEXT_FONTS,
  DEFAULT_MAX_ZOOM,
} from '../../constants';
import { isPoint, isLineString, isPolygon } from '../geom';
import { isPolygonOutlineMapboxLayer } from './types';
import { getSymbolColorExpression } from '../style';

const METERS_TO_FEET = 3.28084;

export const getMapboxLabelTextFieldProperty = (layer, useImperialUnits) => {
  const { labels, hideLabelPrefix = false } = layer.style;
  const layerIsLine = isLineString(layer);
  const lengthUnits = useImperialUnits ? ' ft.' : ' m.';

  return [
    'format',
    ...labels.flatMap((label, index) => {
      const attributeValueText = [
        'case',
        ['any', ['==', label, 'Total Length'], ['==', label, 'Length']],
        [
          'coalesce',
          [
            'case',
            useImperialUnits,
            [
              'concat',
              [
                'to-string',
                [
                  'number-format',
                  ['*', ['get', label], METERS_TO_FEET],
                  { locale: 'en-US', 'max-fraction-digits': 2 },
                ],
              ],
              lengthUnits,
            ],
            [
              'concat',
              [
                'to-string',
                ['number-format', ['get', label], { locale: 'en-US', 'max-fraction-digits': 2 }],
              ],
              lengthUnits,
            ],
          ],
          'N/A',
        ],
        ['to-string', ['coalesce', ['get', label], 'N/A']],
      ];

      const labelText = hideLabelPrefix
        ? attributeValueText
        : ['concat', label, ': ', attributeValueText];

      return [
        labelText,
        layerIsLine && labels.length === 2 && index === 0 ? ' - ' : '\n',
        {
          'text-font': ['literal', ['Lato Regular']],
        },
      ];
    }),
  ];
};

function getLabelStyling(layer) {
  const layerIsPoint = isPoint(layer);
  const layerIsLine = isLineString(layer);
  const layerIsPolygon = isPolygon(layer);

  let textVariableAnchor = {};
  let textAnchor = { textAnchor: 'top' };
  let textRadialOffset = {};
  let symbolPlacement = {};
  let styleOverride = {};

  if (layerIsPoint) {
    textVariableAnchor = {
      textVariableAnchor: ['top-right', 'bottom-right', 'top-left', 'bottom-left'],
    };
    textAnchor = {};
    textRadialOffset = { textRadialOffset: 0.5 };
  } else if (layerIsLine) {
    symbolPlacement = { symbolPlacement: 'line-center' };
  } else if (layerIsPolygon) {
    textAnchor = { textAnchor: 'center' };
    styleOverride = {
      textSize: ['interpolate', ['linear'], ['zoom'], 0, 4, 22, 25],
    };
  }

  return {
    textVariableAnchor,
    textAnchor,
    textRadialOffset,
    symbolPlacement,
    styleOverride,
  };
}

export const getMapboxLabelStyle = ({
  layer,
  visibility = 'visible',
  useImperialUnits = false,
  isGeoJSONSource = false,
  minZoomLevel = DEFAULT_MIN_ZOOM_TO_VIEW_LABELS,
}) => {
  const { labels, labelsEnabled, labelStyle } = layer.style;

  if (!labelsEnabled || labels.length === 0) return null;

  const { textColor, textHaloColor, haloMatchFeatureColor, textSize, textFont, layerZoomLevels } =
    labelStyle;
  const geomType = isPolygonOutlineMapboxLayer(layer) ? 'LineString' : layer.geomType;
  const colorExpression = getSymbolColorExpression(layer.style, geomType, false);
  const haloColor = haloMatchFeatureColor ? colorExpression : textHaloColor;

  const { textVariableAnchor, textAnchor, textRadialOffset, symbolPlacement, styleOverride } =
    getLabelStyling(layer);

  const styleObject = {
    textField: getMapboxLabelTextFieldProperty(layer, useImperialUnits),
    textSize: textSize ?? 12,
    textFont: textFont ? LABEL_TEXT_FONTS[textFont] : LABEL_TEXT_FONTS.Regular,
    textColor: textColor ?? '#000000',
    textHaloColor: haloColor ?? 'rgba(0, 0, 0, 0)', // default transparent
    textHaloWidth: haloColor ? 5 : 0,
    textHaloBlur: haloColor ? 2 : 0,
    ...symbolPlacement,
    ...textVariableAnchor,
    ...textAnchor,
    ...textRadialOffset,
    textJustify: 'auto',
    visibility,
    minzoom: layerZoomLevels ? layerZoomLevels[0] : minZoomLevel,
    maxzoom: layerZoomLevels ? layerZoomLevels[1] : DEFAULT_MAX_ZOOM,
    ...styleOverride,
  };

  styleObject[`${isGeoJSONSource ? 'source' : 'sourceLayer'}`] = `lyr_${layer.id}`;
  return styleObject;
};
